import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import {
  getNodeCameras, deleteCamera, getRecordings,
  getRecordingConfig, updateRecordingConfig,
  getMotionScore, getMotionCapability,
} from '../services/api.service';
import CameraFormModal from '../components/modals/CameraFormModal';
import LiveStreamPlayer from '../components/LiveStreamPlayer';
import { useCameraStream } from '../contexts/NodeStreamContext';
import { useNode } from '../contexts/NodeContext';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeftIcon, PencilIcon, TrashIcon, FireIcon, FilmIcon } from '@heroicons/react/24/outline';
import { RecordingConfigPanel, StatusTag, recordingState, PageTitle, Modal, BTN_SECONDARY, BTN_SECONDARY_SM, BTN_DANGER } from '@trinetra/shared';

export default function CameraDetailPage() {
  const { nodeId, cameraId } = useParams<{ nodeId: string; cameraId: string }>();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { selectedNode, selectNode } = useNode();

  // Camera data
  const [camera, setCamera] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [recordings, setRecordings] = useState<any[]>([]);

  // Live stream via the shared, session-level connection — no per-page WS/PC.
  // Subscribing/unsubscribing only adds/removes a track on the warm connection,
  // so navigating to/from this page (on the selected node) is instant.
  const { stream, state: cameraState, onStalled, onPlaying, onRetry } = useCameraStream(nodeId, cameraId);

  // Edit state
  const [showEdit, setShowEdit] = useState(false);

  // `?edit=1` opens the edit dialog on arrival, so surfaces that only hold a
  // camera id (the Multi-Site grid's tile actions) can link straight to the form
  // instead of dropping the user here to hunt for the button. The flag is dropped
  // from the URL once consumed — otherwise Back, or a reload, reopens the dialog.
  const [searchParams, setSearchParams] = useSearchParams();
  const wantsEdit = searchParams.get('edit') === '1';
  useEffect(() => {
    if (!wantsEdit || !camera || !hasPermission('cameras.edit')) return;
    setShowEdit(true);
    const next = new URLSearchParams(searchParams);
    next.delete('edit');
    setSearchParams(next, { replace: true });
  }, [wantsEdit, camera, hasPermission, searchParams, setSearchParams]);

  // Delete state
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Recording mode configuration — the same shared panel the local dashboard renders, so the
  // two can't drift. Only the transport differs (tunnel vs direct).
  const [recConfig, setRecConfig] = useState<any>(null);
  const [recMeta, setRecMeta] = useState<any>({ defaults: {}, options: {}, bitrate_kbps: null });
  const [recLoading, setRecLoading] = useState(false);
  const [recError, setRecError] = useState<string | null>(null);
  const [recNotice, setRecNotice] = useState<string | null>(null);
  const [motion, setMotion] = useState<any>(null);
  const [motionCap, setMotionCap] = useState<any>(null);

  async function loadRecordingConfig() {
    if (!nodeId || !cameraId) return;
    setRecLoading(true);
    setRecError(null);
    try {
      const res: any = await getRecordingConfig(nodeId, cameraId);
      setRecConfig(res.config);
      setRecMeta({
        defaults: res.defaults || {},
        options: res.options || {},
        bitrate_kbps: res.bitrate_kbps ?? null,
      });
    } catch (err: any) {
      setRecError(err.response?.data?.error || err.message || 'Failed to load recording configuration');
    } finally {
      setRecLoading(false);
    }
  }

  useEffect(() => { loadRecordingConfig(); }, [nodeId, cameraId]);

  // Live motion meter — polled only while the camera is actually in a motion mode. Each poll
  // is a tunnel round trip, so 3s here rather than the local dashboard's 2s.
  const motionRelevant = recConfig?.enabled && (recConfig?.mode === 'motion' || recConfig?.mode === 'event_motion');
  useEffect(() => {
    if (!nodeId || !cameraId || !motionRelevant) { setMotion(null); return; }
    let cancelled = false;
    const poll = () => {
      getMotionScore(nodeId, cameraId)
        .then((m: any) => { if (!cancelled) setMotion(m); })
        .catch(() => { if (!cancelled) setMotion({ state: 'unavailable', score: null }); });
    };
    poll();
    const t = setInterval(poll, 3000);
    getMotionCapability(nodeId, cameraId)
      .then((c: any) => { if (!cancelled) setMotionCap(c); })
      .catch(() => { if (!cancelled) setMotionCap(null); });
    return () => { cancelled = true; clearInterval(t); };
  }, [nodeId, cameraId, motionRelevant]);

  async function handleSaveRecordingConfig(patch: any) {
    if (!nodeId || !cameraId) return;
    setRecLoading(true);
    setRecError(null);
    setRecNotice(null);
    try {
      const res: any = await updateRecordingConfig(nodeId, cameraId, patch);
      setRecConfig(res.config);
      setRecMeta((prev: any) => ({ ...prev, defaults: res.defaults || prev.defaults }));
      setRecNotice('Recording configuration saved');
      setTimeout(() => setRecNotice(null), 3000);
    } catch (err: any) {
      const data = err.response?.data;
      setRecError(data?.details?.[0]?.message || data?.error || err.message || 'Save failed');
    } finally {
      setRecLoading(false);
    }
  }

  // ── Fetch camera data ──
  useEffect(() => {
    if (!nodeId || !cameraId) return;
    setLoading(true);
    getNodeCameras(nodeId)
      .then((data) => {
        const cams = Array.isArray(data) ? data : data?.data || data?.cameras || [];
        const cam = cams.find((c: any) => (c.id || c.camera_id) === cameraId);
        setCamera(cam || null);
      })
      .catch(() => setCamera(null))
      .finally(() => setLoading(false));

    getRecordings(nodeId, { camera_id: cameraId, limit: 10 })
      .then((data) => {
        const recs = Array.isArray(data) ? data : data?.data || data?.recordings || [];
        setRecordings(recs);
      })
      .catch(() => setRecordings([]));
  }, [nodeId, cameraId]);

  // Refetch camera
  async function refetchCamera() {
    if (!nodeId || !cameraId) return;
    const data = await getNodeCameras(nodeId);
    const cams = Array.isArray(data) ? data : data?.data || data?.cameras || [];
    setCamera(cams.find((c: any) => (c.id || c.camera_id) === cameraId) || null);
  }

  // Dismissing also clears the error, so reopening the dialog doesn't lead with last
  // attempt's failure.
  const closeDeleteConfirm = useCallback(() => {
    setShowDeleteConfirm(false);
    setDeleteError(null);
  }, []);

  // Escape closes it. A confirmation with no keyboard exit is the one modal where that
  // omission is most likely to be noticed — the reader is already hesitating.
  useEffect(() => {
    if (!showDeleteConfirm) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && !deleteLoading) closeDeleteConfirm(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [showDeleteConfirm, deleteLoading, closeDeleteConfirm]);

  async function handleDelete() {
    if (!nodeId || !cameraId) return;
    setDeleteLoading(true);
    setDeleteError(null);
    try {
      await deleteCamera(nodeId, cameraId);
      navigate('/cameras', { replace: true });
    } catch (err: any) {
      // Was swallowed into `catch { /* ignore */ }`: the dialog just sat there after the
      // click, which reads as a frozen button rather than a refusal — and over the tunnel a
      // delete has more ways to fail than it does locally.
      setDeleteError(err?.response?.data?.message || err?.message || 'Could not delete this camera');
    } finally {
      setDeleteLoading(false);
    }
  }

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-brand" />
      </div>
    );
  }

  if (!camera) {
    return <p className="py-16 text-center text-muted-foreground">Camera not found.</p>;
  }

  const cameraOnline = camera.status === 'active' || camera.status === 'streaming' || camera.status === 'recording';
  // A camera that isn't connected has no pipeline running, whatever its mode says.
  const recState = recordingState(recConfig, !cameraOnline);

  return (
    <div className="space-y-3">
      {/* Title bar: back + camera name, model/location, then the page actions. Labels hide
          below xl so five actions plus the status badge fit beside the name. */}
      <PageTitle
        title={(
          <>
            <button type="button" onClick={() => navigate('/cameras')} aria-label="Back to cameras" className="mr-1.5 inline-flex rounded p-0.5 align-[-3px] text-muted-foreground hover:bg-muted hover:text-foreground">
              <ArrowLeftIcon className="h-4 w-4" />
            </button>
            <span title={camera.name}>{camera.name}</span>
          </>
        )}
        subtitle={`${camera.manufacturer || 'Unknown'} ${camera.model || ''} — ${camera.location || 'No location'}`}
      >
        {/* Straight to this camera's playback. /recordings reads ?camera=<id>, but it
            resolves the camera against the NODE FROM CONTEXT, not the URL — this page is
            addressed by :nodeId, so the two can disagree (arriving by deep link or back
            button). Align the context first or the page opens on the wrong node and
            silently falls back to its camera picker. */}
        {hasPermission('recordings.view') && (
          <button
            onClick={() => {
              if (nodeId && selectedNode?.nodeId !== nodeId) selectNode(nodeId);
              navigate(`/recordings?camera=${cameraId}`);
            }}
            title="Recordings"
            data-tour="camera-detail-recordings"
            className={BTN_SECONDARY_SM}
          >
            <FilmIcon className="h-4 w-4" />
            <span className="hidden xl:inline">Recordings</span>
          </button>
        )}
        <button
          onClick={() => navigate(`/nodes/${nodeId}/cameras/${cameraId}/heatmap?name=${encodeURIComponent(camera?.name || 'Camera')}`)}
          title="Heatmap"
          data-tour="camera-detail-heatmap"
          className="flex h-8 items-center gap-1.5 rounded-lg border border-orange-200 px-3 text-[12px] font-medium text-orange-600 hover:bg-orange-50 dark:border-orange-800/50 dark:text-orange-400 dark:hover:bg-orange-900/20 transition-colors"
        >
          <FireIcon className="h-4 w-4" />
          <span className="hidden xl:inline">Heatmap</span>
        </button>
        {hasPermission('cameras.edit') && (
        <button
          onClick={() => setShowEdit(true)}
          title="Edit"
          data-tour="camera-detail-edit"
          className={BTN_SECONDARY_SM}
        >
          <PencilIcon className="h-4 w-4" />
          <span className="hidden xl:inline">Edit</span>
        </button>
        )}
        {hasPermission('cameras.delete') && (
        <button
          onClick={() => setShowDeleteConfirm(true)}
          title="Delete"
          className="flex h-8 items-center gap-1.5 rounded-lg border border-red-200 px-3 text-[12px] font-medium text-destructive hover:bg-red-50 dark:border-red-800/50 dark:hover:bg-red-900/20 transition-colors"
        >
          <TrashIcon className="h-4 w-4" />
          <span className="hidden xl:inline">Delete</span>
        </button>
        )}
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-semibold ${
          camera.status === 'active' || camera.status === 'streaming'
            ? 'bg-emerald-500/10 text-emerald-500 border border-emerald-500/30'
            : 'bg-muted text-muted-foreground border border-border'
        }`}>
          <span className={`h-1.5 w-1.5 rounded-full ${
            camera.status === 'active' || camera.status === 'streaming' ? 'bg-emerald-400' : 'bg-muted-foreground'
          }`} />
          {camera.status || 'offline'}
        </span>
      </PageTitle>

      {/* Edit Camera Modal */}
      <CameraFormModal
        isOpen={showEdit}
        onClose={() => setShowEdit(false)}
        onSuccess={() => { setShowEdit(false); refetchCamera(); }}
        camera={camera}
        nodeId={nodeId || ''}
      />

      {/* Delete Confirmation Modal — Escape / backdrop dismiss, except while the delete runs. */}
      <Modal
        open={showDeleteConfirm}
        onClose={() => { if (!deleteLoading) closeDeleteConfirm(); }}
        title="Delete Camera"
        size="sm"
        footer={
          <div className="flex justify-end gap-2">
            <button onClick={closeDeleteConfirm} disabled={deleteLoading} className={BTN_SECONDARY}>Cancel</button>
            <button onClick={handleDelete} disabled={deleteLoading} className={BTN_DANGER}>
              {deleteLoading ? 'Deleting…' : 'Delete'}
            </button>
          </div>
        }
      >
        <div className="px-5 py-4">
          <p className="text-[13px] text-muted-foreground">
            Are you sure you want to delete <span className="font-semibold text-foreground">{camera.name}</span>? This action cannot be undone.
          </p>
          {deleteError && (
            <p className="mt-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-[12px] text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300" role="alert">
              {deleteError}
            </p>
          )}
        </div>
      </Modal>

      {/* Player + Details */}
      <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <div data-tour="camera-detail-player" className="aspect-video w-full overflow-hidden rounded-lg bg-black">
            <LiveStreamPlayer
              stream={stream}
              label={camera.name}
              connectionState={cameraState}
              fillContainer
              onRetry={onRetry}
              onStalled={onStalled}
              onPlaying={onPlaying}
            />
          </div>
        </div>
        <div className="space-y-4">
          {/* Recording state. This card used to hard-code a pulsing dot and a green
              "Always On" pill for EVERY camera — including ones with recording switched off,
              on a triggered mode, or plainly offline — while the real config sat in state
              three lines away. Named for what it holds, and reading from that config. */}
          {hasPermission('recordings.view') && (
            <div className="rounded-xl border border-border bg-card p-5 space-y-3 shadow-card">
              <h3 className="text-[14px] font-semibold text-foreground">Recording</h3>
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  {/* Pulsing = a pipeline is actually running. */}
                  {recState.live ? (
                    <span className="relative flex h-2 w-2">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-75"></span>
                      <span className="relative inline-flex h-2 w-2 rounded-full bg-red-500"></span>
                    </span>
                  ) : (
                    // Inline colour, not `bg-muted-foreground/50`: Tailwind v3 drops opacity
                    // modifiers on bare `var()` colours, leaving an invisible dot.
                    <span
                      className="inline-flex h-2 w-2 rounded-full"
                      style={{ backgroundColor: 'color-mix(in srgb, var(--muted-foreground) 50%, transparent)' }}
                    />
                  )}
                  <span className="text-[13px] font-medium text-foreground">Status</span>
                </div>
                <StatusTag tone={recState.tone}>{recState.label}</StatusTag>
              </div>
              {/* The switch and every other recording setting live in the panel below, which
                  is off-screen on a tall page — so say where to go rather than leaving the
                  reader to scroll and hope. */}
              {hasPermission('recordings.config') && (
                <a
                  href="#recording-config"
                  className="inline-block text-[12px] font-medium text-brand hover:underline"
                >
                  {recState.label === 'Off' ? 'Turn recording on' : 'Change recording settings'} ↓
                </a>
              )}
            </div>
          )}

          {/* Details */}
          <div data-tour="camera-detail-info" className="rounded-xl border border-border bg-card p-5 space-y-2 text-[13px] shadow-card">
            <h3 className="text-[14px] font-semibold text-foreground">Details</h3>
            <div className="flex justify-between text-muted-foreground">
              <span>IP</span>
              <span className="font-mono text-foreground">{camera.ip_address || '—'}</span>
            </div>
            <div className="flex justify-between text-muted-foreground">
              <span>Protocol</span>
              <span className="text-foreground">{camera.protocol || 'RTSP'}</span>
            </div>
            <div className="flex justify-between text-muted-foreground">
              <span>Manufacturer</span>
              <span className="text-foreground">{camera.manufacturer || '—'}</span>
            </div>
            <div className="flex justify-between text-muted-foreground">
              <span>Model</span>
              <span className="text-foreground">{camera.model || '—'}</span>
            </div>
            <div className="flex justify-between text-muted-foreground">
              <span>Location</span>
              <span className="text-foreground">{camera.location || '—'}</span>
            </div>
            {camera.groups && camera.groups.length > 0 && (
              <div className="pt-1">
                <span className="text-muted-foreground">Groups</span>
                <div className="mt-1 flex flex-wrap gap-1">
                  {camera.groups.map((g: any) => (
                    <span key={g.id} className="inline-block rounded-md bg-brand/10 px-2 py-0.5 text-[11px] font-medium text-brand dark:bg-brand/20">{g.name}</span>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Connection Info */}
          <div className="rounded-xl border border-border bg-card p-5 space-y-2 text-[13px] shadow-card">
            <h3 className="text-[14px] font-semibold text-foreground">Connection</h3>
            <div className="flex justify-between text-muted-foreground">
              <span>WebRTC</span>
              <span className={`font-medium ${cameraState === 'rtc_established' ? 'text-emerald-500' : cameraState === 'signaling' || cameraState === 'waiting_stream' || cameraState === 'pending' || cameraState === 'reconnecting' ? 'text-amber-500' : 'text-destructive'}`}>
                {cameraState}
              </span>
            </div>
            <div className="flex justify-between text-muted-foreground">
              <span>Site</span>
              <span className="font-mono text-foreground text-[11px]">{nodeId?.slice(0, 12)}…</span>
            </div>
          </div>
        </div>
      </div>

      {/* Recording configuration — mode, triggers, retention */}
      {hasPermission('recordings.view') && (
        <div id="recording-config" data-tour="camera-detail-recording-config" className="space-y-3 scroll-mt-4">
          {recError && (
            <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 dark:border-red-800 dark:bg-red-900/20">
              <p className="flex-1 text-[13px] font-medium text-red-700 dark:text-red-300">{recError}</p>
              <button
                onClick={() => setRecError(null)}
                className="text-lg leading-none text-red-400 hover:text-red-600 dark:hover:text-red-200"
              >
                &times;
              </button>
            </div>
          )}
          {recNotice && (
            <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-[13px] font-medium text-emerald-700 dark:border-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-300">
              ✓ {recNotice}
            </div>
          )}
          <RecordingConfigPanel
            config={recConfig}
            defaults={recMeta.defaults}
            options={recMeta.options}
            bitrateKbps={recMeta.bitrate_kbps}
            canEdit={hasPermission('recordings.config')}
            loading={recLoading}
            motion={motion}
            motionCapability={motionCap}
            onSave={handleSaveRecordingConfig}
            eventModeAvailable
            motionModeAvailable
          />
        </div>
      )}

      {/* Recent Recordings */}
      {recordings.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <h2 className="text-[15px] font-semibold text-foreground">Recent Recordings</h2>
            <span className="text-[12px] text-muted-foreground">{recordings.length} recording{recordings.length !== 1 ? 's' : ''}</span>
          </div>
          <div className="rounded-xl border border-border bg-card shadow-card overflow-hidden">
            <table className="w-full text-[13px]">
              <thead>
                <tr className="border-b border-border bg-muted/50">
                  <th className="px-4 py-2.5 text-left font-semibold text-foreground">Start</th>
                  <th className="px-4 py-2.5 text-left font-semibold text-foreground">Duration</th>
                  <th className="px-4 py-2.5 text-left font-semibold text-foreground">Size</th>
                </tr>
              </thead>
              <tbody>
                {recordings.map((rec: any) => (
                  <tr key={rec.id} className="border-b border-border hover:bg-muted/30 transition-colors">
                    <td className="px-4 py-2.5 text-muted-foreground">{rec.start_time ? new Date(rec.start_time).toLocaleString() : '—'}</td>
                    <td className="px-4 py-2.5 text-muted-foreground">{rec.duration ? `${Math.round(rec.duration)}s` : '—'}</td>
                    <td className="px-4 py-2.5 text-muted-foreground">{rec.file_size ? `${(rec.file_size / 1048576).toFixed(1)} MB` : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
